import React, { memo, useCallback } from 'react';
import DiscardConfirmation, {
  useDiscardConfirmation,
} from 'components/DiscardConfirmation';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import { EditMembersAndRelations } from 'pages/configuration/objectGeneration/components/EditMembersAndRelations';
import {
  onDiscardForm,
  requestDeleteTemplate,
  requestUpdateTemplate,
  selectFirstDrawer,
  selectGroupCreationMode,
  selectSecondDrawer,
  selectTemplatesMap,
  updateDrawerTemplate,
} from 'slices/objectGenerationTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TActivityDraftGroupConfig } from '@timeedit/types/lib/types/activityGroup.type';
import { TEDrawer } from '@timeedit/ui-components';
import FieldSection from './sections/Field';
import GeneralSettings from './sections/GeneralInfo';

const ObjectGenerationDrawer = () => {
  const dispatch = useAppDispatch();

  const firstDrawer = useAppSelector(selectFirstDrawer);
  const secondDrawer = useAppSelector(selectSecondDrawer);
  const creationMode = useAppSelector(selectGroupCreationMode);

  const templatesMap = useAppSelector(selectTemplatesMap);

  const setFirstDrawer = useCallback(
    (data: TActivityDraftGroupConfig) => {
      dispatch(
        updateDrawerTemplate({
          isFirstDrawer: true,
          data,
          groupCreationMode: creationMode,
        })
      );
    },
    [dispatch, creationMode]
  );

  const setSecondDrawer = useCallback(
    (data: TActivityDraftGroupConfig) => {
      dispatch(
        updateDrawerTemplate({
          isFirstDrawer: false,
          data,
          groupCreationMode: creationMode,
        })
      );
    },
    [dispatch, creationMode]
  );

  useSelectResource(
    'objectId',
    templatesMap,
    firstDrawer,
    secondDrawer,
    setFirstDrawer,
    setSecondDrawer
  );

  const onUpdateDrawer = async (
    isFirstDrawer: boolean,
    data: { key: string; value: any }
  ) => {
    const drawer = isFirstDrawer ? firstDrawer : secondDrawer;
    if (!drawer) return;
    const updatedData = { ...drawer, [data.key]: data.value, isEdited: true };
    if (isFirstDrawer) {
      dispatch(
        updateDrawerTemplate({
          isFirstDrawer: true,
          data: updatedData,
        })
      );
      return;
    }
    dispatch(
      updateDrawerTemplate({
        isFirstDrawer: false,
        data: updatedData,
      })
    );
  };

  const handleSaveFirstDrawer = async () => {
    dispatch(
      requestUpdateTemplate({
        isFirstDrawer: true,
        data: firstDrawer,
        creationMode,
      })
    );
  };

  const handleSaveSecondDrawer = async () => {
    dispatch(
      requestUpdateTemplate({
        isFirstDrawer: false,
        data: secondDrawer,
        creationMode,
      })
    );
  };

  const delFirstDrawer = async () => {
    const delResult = await dispatch(requestDeleteTemplate(firstDrawer?.id));
    if (delResult) {
      setFirstDrawer(null);
    }
  };

  const delSecondDrawer = async () => {
    const delResult = await dispatch(requestDeleteTemplate(secondDrawer?.id));
    if (delResult) {
      setSecondDrawer(null);
    }
  };

  const [
    openDiscardDialog,
    onCloseDiscardDialog,
    confirmFn,
    confirmDiscardChanged,
  ] = useDiscardConfirmation();

  return (
    <>
      <TEDrawer
        expandWidth={'70vw'}
        firstDrawer={{
          title: firstDrawer?.name,
          content: (
            <Content
              onChange={async (data) => {
                await onUpdateDrawer(true, data);
              }}
              data={firstDrawer}
            />
          ),
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText('template_confirmation_delete')}
              handleDelete={delFirstDrawer}
              handleDiscard={() =>
                dispatch(
                  onDiscardForm({ isFirstDrawer: true, id: firstDrawer?.id })
                )
              }
              handleSave={handleSaveFirstDrawer}
              isSaveDisable={!firstDrawer?.isEdited as any}
              showDeleteAtAll={!creationMode}
            />
          ),
          drawerOpen: !!firstDrawer,
          setDrawerOpen: (val) => {
            if (!val && firstDrawer?.isEdited) {
              confirmDiscardChanged(() => {
                setFirstDrawer(null);
              });
              return false;
            }

            setFirstDrawer(null);
          },
        }}
        secondDrawer={{
          title: secondDrawer?.name,
          content: (
            <Content
              onChange={async (data) => {
                await onUpdateDrawer(false, data);
              }}
              data={secondDrawer}
            />
          ),
          drawerOpen: !!secondDrawer,
          setDrawerOpen: (val) => {
            if (!val && secondDrawer?.isEdited) {
              confirmDiscardChanged(() => {
                setSecondDrawer(null);
              });
              return false;
            }
            setSecondDrawer(null);
          },
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText('template_confirmation_delete')}
              handleDelete={delSecondDrawer}
              handleDiscard={() =>
                dispatch(
                  onDiscardForm({ isFirstDrawer: false, id: secondDrawer?.id })
                )
              }
              handleSave={handleSaveSecondDrawer}
              isSaveDisable={!secondDrawer?.isEdited as any}
              showDeleteAtAll={!creationMode}
            />
          ),
        }}
        onExpand={() => null}
      />
      <DiscardConfirmation
        open={openDiscardDialog}
        onClose={onCloseDiscardDialog}
        confirmFn={confirmFn}
      />
    </>
  );
};

type TContent = {
  data: TActivityDraftGroupConfig;
  onChange: (data: { key: string; value: unknown }) => void;
};

const Content = memo(({ data, onChange }: TContent) => {
  const onChangeValue = (key: string, value: unknown) => {
    onChange({ key, value });
  };
  if (!data) return null;
  return (
    <>
      <GeneralSettings value={data} onChangeValue={onChangeValue} />
      <FieldSection value={data} onChangeValue={onChangeValue} />
      <EditMembersAndRelations
        title={getText('memberships')}
        explanationText={getText('group_creation_memberships_explanation')}
        endpoint="/member-types"
        typeKey="reservationKind"
        value={data.memberships ?? []}
        onChange={(ids) => onChangeValue('memberships', ids)}
        activityCreateTemplateId={data.activityCreateTemplateId}
        groupObjectType={data.objectType}
      />
      <EditMembersAndRelations
        title={getText('relations')}
        explanationText={getText('group_creation_relations_explanation')}
        endpoint="/relation-types"
        typeKey="relationType"
        value={data.relations ?? []}
        onChange={(ids) => onChangeValue('relations', ids)}
        activityCreateTemplateId={data.activityCreateTemplateId}
        groupObjectType={data.objectType}
      />
    </>
  );
});

export default ObjectGenerationDrawer;
